import { Employee, ApprovalMemberFilters, conditions, PrismLead } from '@repo/services';
import { TransactionTeamRoles, lowValueTransactionLeadThresholdUsd } from './aimConstants';

/**
 * Finds the executive-level employees (CEO, CFO, VP Land, VP Legal) from a full employee list by filtering to `jobCategory === 'EXEC'` and matching each flag.
 *
 * @param employees - The full employee list to search.
 * @returns An object with the `ceo`, `cfo`, `vpLand`, and `vpLegal` employees, or `undefined` for any not found.
 */
export const filterExecutives = (employees: Employee[]) => {
    const allExecs = employees?.filter((e) => e.jobCategory === 'EXEC');
    return {
        ceo: allExecs?.find((employee) => employee.isCEO),
        cfo: allExecs?.find((employee) => employee.isCFO),
        vpLand: allExecs?.find((employee) => employee.jobCategory === 'EXEC' && employee.isLand),
        vpLegal: allExecs?.find((employee) => employee.jobCategory === 'EXEC' && employee.isLegal)
    };
};

/**
 * Filters a list of prospective approval members down to those eligible for the given transaction team role, checking `validForTransactionLead`/`validForFinanceLead`/`validForInvestmentAnalyst` depending on whether `role` is land, finance, or operations.
 *
 * For the land (Transaction Lead) role, when `transactionTotalUsd` is provided and
 * falls below `lowValueTransactionLeadThresholdUsd` (exclusive), the eligible pool is
 * widened to also include leads flagged `validForTransactionLeadLowValue`. The result
 * is de-duplicated by `id`. A missing/unknown total price is treated as NOT low-value,
 * so the standard `validForTransactionLead`-only list is returned.
 *
 * @param names - Candidate approval members (leads) to filter.
 * @param role - The transaction team role to filter eligibility for (land/finance/operations); if omitted or unrecognized, no members match.
 * @param transactionTotalUsd - Optional transaction total price in USD, used only for the land role to decide low-value eligibility.
 * @returns The subset of `names` valid for the given role.
 */
export const filterApprovalMembersList = (
  names: PrismLead[],
  role?: string,
  transactionTotalUsd?: number | null
) => {
  const isLowValue =
    transactionTotalUsd != null &&
    transactionTotalUsd < lowValueTransactionLeadThresholdUsd;

  const filteredNames = names.filter((n) => {
      if (role === TransactionTeamRoles.land) {
        return isLowValue
          ? n.validForTransactionLead || n.validForTransactionLeadLowValue
          : n.validForTransactionLead;
      }
      if (role === TransactionTeamRoles.finance) {
        return n.validForFinanceLead;
      }
      if (role === TransactionTeamRoles.operations) {
        return n.validForInvestmentAnalyst;
      }
    });

    // De-duplicate by id (a lead may match more than once via the low-value union).
    const seen = new Set<number>();
    return filteredNames.filter((n) => {
      if (seen.has(n.id)) return false;
      seen.add(n.id);
      return true;
    });
};
